import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { trackCTAClick, trackPhoneClick } from './analytics';

/**
 * Chicago Location Page
 * 
 * Optimized for local SEO keywords:
 * - general contractor chicago
 * - construction companies chicago
 * - commercial construction chicago
 * - chicago general contractors
 */
function ChicagoPage() {
  const neighborhoods = [
    'Loop', 'River North', 'West Loop', 'South Loop', 'Streeterville',
    'Gold Coast', 'Lincoln Park', 'Wicker Park', 'Logan Square', 'Pilsen',
    'Bridgeport', 'Hyde Park', 'Bronzeville', 'Fulton Market', 'Old Town'
  ];

  const suburbs = [
    { name: 'Naperville', distance: '30 miles' },
    { name: 'Aurora', distance: '40 miles' },
    { name: 'Schaumburg', distance: '25 miles' },
    { name: 'Oak Brook', distance: '15 miles' },
    { name: 'Evanston', distance: '12 miles' },
    { name: 'Skokie', distance: '15 miles' },
    { name: 'Des Plaines', distance: '18 miles' },
    { name: 'Arlington Heights', distance: '25 miles' },
    { name: 'Joliet', distance: '45 miles' },
    { name: 'Downers Grove', distance: '22 miles' },
    { name: 'Elmhurst', distance: '16 miles' },
    { name: 'Wheaton', distance: '25 miles' }
  ];

  // Local Business Schema
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'GeneralContractor',
    name: 'Freedom Contracting Group',
    description: 'Commercial construction company and general contractor serving Chicago and Illinois',
    url: 'https://freedomcg.net',
    telephone: '+1-312-675-6000',
    email: 'hello@freedomcg.net',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '111 W Jackson Blvd, Suite 1700',
      addressLocality: 'Chicago',
      addressRegion: 'IL',
      postalCode: '60604',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '41.8781',
      longitude: '-87.6298'
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Chicago',
        containedInPlace: {
          '@type': 'State',
          name: 'Illinois'
        }
      },
      ...suburbs.map(suburb => ({
        '@type': 'City',
        name: suburb.name,
        containedInPlace: {
          '@type': 'State',
          name: 'Illinois'
        }
      }))
    ],
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '07:00',
      closes: '18:00'
    },
    priceRange: '$$$$',
    paymentAccepted: 'Cash, Check, Credit Card',
    currenciesAccepted: 'USD'
  };

  // FAQ Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Where is Freedom Contracting Group located in Chicago?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Freedom Contracting Group is headquartered at 111 W Jackson Blvd, Suite 1700, Chicago, IL 60604, in the heart of downtown Chicago\'s business district.'
        }
      },
      {
        '@type': 'Question',
        name: 'What areas in Chicago does Freedom Contracting Group serve?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We serve all of Chicago including the Loop, River North, West Loop, South Loop, Streeterville, Lincoln Park, and all surrounding neighborhoods. We also serve suburban areas including Naperville, Aurora, Schaumburg, Oak Brook, and throughout the Chicagoland region.'
        }
      },
      {
        '@type': 'Question',
        name: 'Is Freedom Contracting Group a licensed general contractor in Illinois?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, Freedom Contracting Group is fully licensed, bonded, and insured to perform commercial construction work in the State of Illinois and the City of Chicago.'
        }
      },
      {
        '@type': 'Question',
        name: 'How do I get a quote for commercial construction in Chicago?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Contact us at (312) 675-6000 or email hello@freedomcg.net for a free consultation and estimate. We can discuss your project requirements and provide a detailed proposal.'
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <SEO 
        title="General Contractor Chicago | Commercial Construction Company"
        description="Freedom Contracting Group is Chicago's trusted general contractor for commercial construction. Office buildings, retail, industrial, healthcare. Licensed & insured. Call (312) 675-6000."
        keywords="general contractor chicago, commercial construction chicago, construction companies chicago, chicago general contractors, commercial contractor chicago, building contractor chicago, general contracting chicago"
        path="/chicago"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Chicago', url: '/chicago' }
        ]}
        additionalSchema={[localBusinessSchema, faqSchema]}
      />

      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">
                <span className="text-red-600">Chicago</span>
                <span className="text-blue-900"> General Contractor</span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">Commercial Construction Services</p>
            </div>
            <Link
              to="/"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Back to Home"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 via-gray-900 to-red-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Chicago's Trusted General Contractor
              </h2>
              <p className="text-xl text-gray-200 leading-relaxed mb-8">
                Freedom Contracting Group is a leading commercial construction company serving 
                Chicago and the entire Chicagoland area. With over 20 years of experience and 
                1,000+ completed projects, we deliver quality craftsmanship with integrity.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="tel:+13126756000"
                  className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-xl transition-all"
                  onClick={() => trackPhoneClick('312-675-6000', 'chicago_hero')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                  (312) 675-6000
                </a>
                <Link 
                  to="/services"
                  className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-8 rounded-xl transition-all"
                  onClick={() => trackCTAClick('view_services', 'chicago_hero')}
                >
                  Our Services
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold mb-4">Our Chicago Office</h3>
                <div className="space-y-4 text-gray-200">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    <div>
                      <p className="font-medium text-white">Address</p>
                      <p>111 W Jackson Blvd, Suite 1700</p>
                      <p>Chicago, IL 60604</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <div>
                      <p className="font-medium text-white">Hours</p>
                      <p>Monday - Friday: 7:00 AM - 6:00 PM</p>
                      <p>Saturday - Sunday: By Appointment</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services in Chicago */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              Commercial Construction Services in Chicago
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As a full-service general contractor, we provide comprehensive construction services 
              for businesses throughout Chicago and Illinois.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Commercial Building Construction',
                description: 'Office buildings, retail centers, and mixed-use developments throughout Chicago.',
                link: '/services/commercial-construction'
              },
              {
                title: 'Office Remodeling',
                description: 'Tenant improvements and office build-outs for Chicago businesses.',
                link: '/services/office-remodeling'
              },
              {
                title: 'Industrial Construction',
                description: 'Warehouses and manufacturing facilities in the Chicago industrial corridor.',
                link: '/services/industrial-construction'
              },
              {
                title: 'Healthcare Construction',
                description: 'Medical offices, clinics, and healthcare facilities in Chicago.',
                link: '/services/healthcare-construction'
              },
              {
                title: 'Retail Construction',
                description: 'Retail stores and restaurant construction in Chicago neighborhoods.',
                link: '/services/retail-construction'
              },
              {
                title: 'Construction Management',
                description: 'Professional project management for Chicago construction projects.',
                link: '/services/project-management'
              }
            ].map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="bg-slate-50 hover:bg-blue-50 rounded-xl p-6 transition-all group"
              >
                <h3 className="text-xl font-bold text-blue-900 group-hover:text-red-600 mb-3 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <span className="inline-flex items-center gap-2 text-blue-900 group-hover:text-red-600 font-semibold transition-colors">
                  Learn More
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Chicago Neighborhoods */}
      <div className="py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
            Serving All Chicago Neighborhoods
          </h2>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <p className="text-gray-700 mb-6 text-center">
              Freedom Contracting Group provides commercial construction services throughout Chicago, including:
            </p>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4 text-center">
              {neighborhoods.map((neighborhood, index) => (
                <div key={index} className="py-2 px-3 bg-blue-50 rounded-lg text-blue-900 text-sm font-medium">
                  {neighborhood}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Suburban Areas */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
            Also Serving Chicago Suburbs
          </h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {suburbs.map((suburb, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-4 flex justify-between items-center">
                <span className="font-medium text-blue-900">{suburb.name}, IL</span>
                <span className="text-sm text-gray-500">{suburb.distance}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-blue-900 mb-4">
              Why Chicago Businesses Choose Freedom Contracting Group
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Local Expertise',
                description: 'Deep knowledge of Chicago building codes, permits, and regulations',
                icon: '🏙️'
              },
              {
                title: 'Licensed & Insured',
                description: 'Fully licensed general contractor in Illinois and Chicago',
                icon: '📋'
              },
              {
                title: '20+ Years Experience',
                description: 'Decades of commercial construction experience in Chicago',
                icon: '⏱️'
              },
              {
                title: '1,000+ Projects',
                description: 'Proven track record with over 1,000 completed projects',
                icon: '✅'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg text-center">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-blue-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqSchema.mainEntity.map((faq, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-3">{faq.name}</h3>
                <p className="text-gray-700">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-900 to-red-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Chicago Construction Project?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Contact Freedom Contracting Group today for a free consultation and estimate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+13126756000"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-xl transition-all shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
              </svg>
              Call (312) 675-6000
            </a>
            <a 
              href="mailto:hello@freedomcg.net"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold py-4 px-8 rounded-xl transition-all"
            >
              Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChicagoPage;
